import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Pencil, Trash2, Loader2, Sparkles, Check, BookOpen } from "lucide-react";
import { BehaviorDescriptionDialog } from "./BehaviorDescriptionDialog";
import { WorkProductsEditor } from "./WorkProductsEditor";
import { InputsEditor } from "./InputsEditor";

function MarcoFeedback({ result, onUse }) {
  return (
    <div className="border border-amber-300 bg-amber-50 p-3 text-sm space-y-2" data-testid="marco-feedback">
      <div className="font-mono text-[11px] uppercase tracking-widest text-amber-800 flex items-center gap-1">
        <Sparkles className="h-3 w-3" /> Niet MARCO-conform
      </div>
      <p className="text-amber-900 whitespace-pre-line" data-testid="marco-feedback-text">{result.feedback}</p>
      {result.suggestion && (
        <div className="flex items-start justify-between gap-2 bg-white border border-amber-200 p-2">
          <span className="text-zinc-800" data-testid="marco-suggestion">{result.suggestion}</span>
          <Button size="sm" variant="outline" className="rounded-none h-7 shrink-0" onClick={() => onUse(result.suggestion)} data-testid="use-marco-suggestion-button">
            Gebruik
          </Button>
        </div>
      )}
    </div>
  );
}

function BehaviorInput({ initial = "", placeholder, onSubmit, onCancel, testid }) {
  const [text, setText] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await onSubmit(text.trim());
      if (r.compliant) {
        toast.success("Gedrag is MARCO-conform en opgeslagen");
        setText("");
        onCancel && onCancel();
      } else setResult(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Controle mislukt");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder={placeholder} className="rounded-none border-zinc-400 h-9" data-testid="behavior-input" />
        <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700 h-9" onClick={submit} disabled={busy} data-testid="submit-behavior-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          <span className="ml-1">{busy ? "AI toetst…" : "Toets & bewaar"}</span>
        </Button>
        {onCancel && (
          <Button variant="outline" className="rounded-none h-9" onClick={onCancel}>
            Annuleren
          </Button>
        )}
      </div>
      {result && <MarcoFeedback result={result} onUse={(s) => { setText(s); setResult(null); }} />}
    </div>
  );
}

export const StrategyCard = ({ strategy, index, onChange, onDeleted, allStrategies }) => {
  const [editBehavior, setEditBehavior] = useState(null);
  const [descFor, setDescFor] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(strategy.name);
  const base = `/strategies/${strategy.id}`;

  const addBehavior = async (text) => {
    const { data } = await api.post(`${base}/behaviors`, { text });
    onChange(data.strategy);
    return data;
  };
  const updateBehavior = (id) => async (text) => {
    const { data } = await api.patch(`${base}/behaviors/${id}`, { text });
    onChange(data.strategy);
    return data;
  };
  const guard = (fn, msg) => async (...a) => {
    try {
      await fn(...a);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || msg);
    }
  };
  const deleteBehavior = guard(async (id) => onChange((await api.delete(`${base}/behaviors/${id}`)).data), "Verwijderen mislukt");
  const saveName = guard(async () => {
    if (!name.trim()) return toast.error("Een strategie moet een naam hebben");
    onChange((await api.patch(base, { name, description: strategy.description })).data);
    setRenaming(false);
  }, "Opslaan mislukt");
  const remove = guard(async () => {
    if (!window.confirm(`Strategie "${strategy.name}" verwijderen?`)) return;
    await api.delete(base);
    toast.success("Strategie verwijderd");
    onDeleted(strategy.id);
  }, "Verwijderen mislukt");

  return (
    <article className="border border-zinc-900 p-5 flex flex-col gap-4" data-testid={`strategy-card-${index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[11px] text-zinc-400">S-{String(index + 1).padStart(2, "0")}</div>
          {renaming ? (
            <div className="flex gap-2 mt-1">
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none h-9" data-testid="strategy-name-input" />
              <Button className="rounded-none bg-zinc-900 h-9" onClick={saveName} data-testid="save-strategy-name-button">OK</Button>
              <Button variant="outline" className="rounded-none h-9" onClick={() => { setName(strategy.name); setRenaming(false); }} data-testid="cancel-strategy-name-button">Annuleren</Button>
            </div>
          ) : (
            <h2 className="font-grotesk text-xl font-bold tracking-tight" data-testid="strategy-name">{strategy.name}</h2>
          )}
          {strategy.description && <p className="text-sm text-zinc-600 mt-1">{strategy.description}</p>}
        </div>
        <div className="flex gap-1 shrink-0">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setRenaming(true)} data-testid="rename-strategy-button"><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={remove} data-testid="delete-strategy-button"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      <div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Gedragingen ({strategy.behaviors.length})</div>
        <ol className="space-y-1.5">
          {strategy.behaviors.map((b, i) =>
            editBehavior === b.id ? (
              <li key={b.id}><BehaviorInput initial={b.text} onSubmit={updateBehavior(b.id)} onCancel={() => setEditBehavior(null)} testid="edit-behavior-form" /></li>
            ) : (
              <li key={b.id} className="flex items-start justify-between gap-2 border-l-2 border-zinc-900 bg-zinc-50 px-3 py-2 text-sm" data-testid="behavior-item">
                <span><span className="font-mono text-zinc-400 mr-2">{i + 1}.</span>{b.text}</span>
                <span className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className={`h-6 w-6 ${b.description_html && b.description_html !== "<p></p>" ? "text-zinc-900" : "text-zinc-400"}`} onClick={() => setDescFor(b.id)} title="Uitgebreide beschrijving" data-testid="behavior-description-button"><BookOpen className="h-3 w-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditBehavior(b.id)} data-testid="edit-behavior-button"><Pencil className="h-3 w-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600" onClick={() => deleteBehavior(b.id)} data-testid="delete-behavior-button"><Trash2 className="h-3 w-3" /></Button>
                </span>
              </li>
            )
          )}
          {strategy.behaviors.length === 0 && <li className="text-sm text-zinc-400">Nog geen gedragingen.</li>}
        </ol>
      </div>
      <BehaviorInput placeholder="Nieuw gedrag, bijv. 'Belt elke klant binnen 24 uur na een klacht terug'" onSubmit={addBehavior} testid="new-behavior-form" />
      <BehaviorDescriptionDialog strategy={strategy} behavior={strategy.behaviors.find((b) => b.id === descFor)} open={!!descFor} onClose={() => setDescFor(null)} onChange={onChange} />
      <div className="border-t border-zinc-200 pt-4">
        <WorkProductsEditor strategy={strategy} onChange={onChange} />
      </div>
      <div className="border-t border-zinc-200 pt-4">
        <InputsEditor strategy={strategy} allStrategies={allStrategies} onChange={onChange} />
      </div>
    </article>
  );
};
